import { type FieldType, type FilterType, inFilterTypes, type ViewConfigPropertyFilter } from '@salescore/core'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { getArrayConditionLabel, isArrayMembershipConditionType } from '../../../../../domain/operatorLabel'

export type FilterFormItemState = Partial<ViewConfigPropertyFilter>

/**
 * filter_type を切り替えたとき、切替先が要求する値の形 (単一の値 / 値のリスト) に filterValue を合わせる。
 *
 * 切替先がリストを要求するのは次のいずれか (それ以外は単一の値を要求する):
 * - array 項目 … 値フォームが常に複数選択なので、全条件がリスト
 * - array 以外の項目 … in / not_in のみリスト
 *
 * array 以外を in / not_in に限定するのは、include などが string 項目では部分一致 (単一の値) を要求し、
 * 一律リスト扱いにすると値が壊れるため。
 *
 * @param filterValue 変換前の値 (単一の値 / 値のリスト / undefined)
 * @param nextFilterType 切替先の filter_type
 * @param fieldType 対象項目の型
 * @returns 切替先の形に合わせた filterValue
 */
export const convertFilterValueOnTypeChange = (
  filterValue: unknown,
  nextFilterType: FilterType,
  fieldType: FieldType | undefined,
): unknown => {
  const requiresValueList = fieldType === 'array' || (inFilterTypes as readonly string[]).includes(nextFilterType)

  // 単一の値 → リスト
  if (typeof filterValue === 'string' && requiresValueList) {
    return [filterValue]
  }
  // リスト → 単一の値 (先頭の値のみ残す。空なら undefined)
  if (Array.isArray(filterValue) && !requiresValueList) {
    return filterValue[0]
  }
  return filterValue
}

export interface ViewConfigFilterNodeForForm {
  logicalOperator: 'and' | 'or'
  leafs: FilterFormItemState[]
  children: ViewConfigFilterNodeForForm[]
}

// 絞り込みは FilterType (equal / less_than 等)、ハイライトと条件表示は CoreDslFormOperator (= / < 等) で、
// 等価・比較演算子の綴りが違う。このため base ラベルの辞書は domain/operatorLabel.ts の getBaseOperatorLabel と
// 統合できず、FilterType 版がここに残っている。
// array 帰属 6 演算子 (overlap / include / in の肯定・否定) は、複数選択項目向けに改名したラベルを
// 絞り込み・ハイライト・条件表示セルの 3 表示層で揃える必要があるため getArrayConditionLabel を共有する。
export const useGetFilterTypeLabel = (): ((fieldType: FieldType | undefined, filterType: FilterType) => string) => {
  const { t } = useTranslation()

  return useCallback(
    (fieldType: FieldType | undefined, filterType: FilterType) => {
      const baseLabel = (filterType: FilterType): string => {
        switch (filterType) {
          case 'in': {
            return t(`次のどれか`)
          }
          case 'not_in': {
            return t(`次のどれでもない`)
          }
          case 'equal': {
            return t(`等しい`)
          }
          case 'not_equal': {
            return t(`異なる`)
          }
          case 'blank': {
            return t(`入力なし`)
          }
          case 'present': {
            return t(`入力あり`)
          }
          case 'null': {
            return t(`入力なし`)
          }
          case 'not_null': {
            return t(`入力あり`)
          }
          case 'include': {
            return t(`次の要素を全て含む`)
          }
          case 'not_include': {
            return t(`次の要素を全て含まない`)
          }
          case 'starts_with': {
            return t(`次の文字列から始まる`)
          }
          case 'not_starts_with': {
            return t(`次の文字列から始まらない`)
          }
          case 'overlap':
          case 'not_overlap': {
            return getArrayConditionLabel(t, filterType)
          }
          case 'less_than': {
            return t(`より小さい`)
          }
          case 'less_than_or_equal': {
            return t(`以下`)
          }
          case 'greater_than': {
            return t(`より大きい`)
          }
          case 'greater_than_or_equal': {
            return t(`以上`)
          }
          case 'equal_me': {
            return t(`自分のユーザーID`)
          }
        }
      }

      switch (fieldType) {
        case undefined:
        case 'boolean':
        case 'numeric':
        case 'integer':
        case 'object':
        case 'time': {
          return baseLabel(filterType)
        }
        case 'date':
        case 'datetime': {
          switch (filterType) {
            case 'less_than': {
              return t('より前')
            }
            case 'less_than_or_equal': {
              return t('以前')
            }
            case 'greater_than': {
              return t('より後')
            }
            case 'greater_than_or_equal': {
              return t('以後')
            }
            default: {
              return baseLabel(filterType)
            }
          }
        }
        case 'array': {
          return isArrayMembershipConditionType(filterType)
            ? getArrayConditionLabel(t, filterType)
            : baseLabel(filterType)
        }
        case 'string': {
          switch (filterType) {
            case 'less_than': {
              return t('より上位')
            }
            case 'greater_than': {
              return t('より下位')
            }
            case 'include': {
              return t(`次の文字列を含む`)
            }
            case 'not_include': {
              return t(`次の文字列を含まない`)
            }
            default: {
              return baseLabel(filterType)
            }
          }
        }
      }
    },
    [t],
  )
}
